import { z } from 'zod'
import { auditFieldsSchema, timestampSchema, uuidSchema } from './common.ts'

export const DEAL_STATUS_CATEGORIES = [
  'not_started',
  'active',
  'successful',
  'unsuccessful',
] as const

export const dealStatusCategorySchema = z.enum(DEAL_STATUS_CATEGORIES)
export type DealStatusCategory = z.infer<typeof dealStatusCategorySchema>

export const DEAL_STATUS_CATEGORY_LABELS: Record<DealStatusCategory, string> = {
  not_started: 'Не розпочато',
  active: 'В роботі',
  successful: 'Завершено успішно',
  unsuccessful: 'Закрито неуспішно',
}

/**
 * Named tokens rather than hex, so a stage cannot be given a colour outside the palette — and so
 * dark mode remains a property of the token rather than of every stage row in the database.
 *
 * Deliberately its own ramp, not the status colours: status means something the system decided,
 * these are labels a person chose. See `--color-stage-*` in the theme.
 */
export const DEAL_STAGE_COLORS = [
  'slate',
  'blue',
  'indigo',
  'violet',
  'pink',
  'red',
  'orange',
  'amber',
  'green',
  'teal',
] as const

export const dealStageColorSchema = z.enum(DEAL_STAGE_COLORS)
export type DealStageColor = z.infer<typeof dealStageColorSchema>

/** Only for the swatch's accessible name — the picker shows colour, not words. */
export const DEAL_STAGE_COLOR_LABELS: Record<DealStageColor, string> = {
  slate: 'Сірий',
  blue: 'Синій',
  indigo: 'Індиго',
  violet: 'Фіолетовий',
  pink: 'Рожевий',
  red: 'Червоний',
  orange: 'Помаранчевий',
  amber: 'Бурштиновий',
  green: 'Зелений',
  teal: 'Бірюзовий',
}

/**
 * The key is the stable identity: saved views, filters and the API all speak it, and it never
 * changes once created. The label is what the admin edits. Keeping them separate is what lets a
 * rename leave every saved view intact.
 */
export const dealStageKeySchema = z
  .string()
  .trim()
  .regex(/^[a-z][a-z0-9_]*$/, 'Ключ може містити лише малі латинські літери, цифри та _')
  .max(48)

export const dealStageDefinitionSchema = z
  .object({
    id: uuidSchema,
    key: dealStageKeySchema,
    label: z.string().min(1),
    category: dealStatusCategorySchema,
    isDefault: z.boolean(),
    color: dealStageColorSchema,
    position: z.number().int().nonnegative(),
    /** Hidden from the board and from stage pickers, without touching the deals already in it. */
    archivedAt: timestampSchema.nullable(),
  })
  .extend(auditFieldsSchema.shape)

export type DealStageDefinition = z.infer<typeof dealStageDefinitionSchema>

export const createDealStageDefinitionInputSchema = z.strictObject({
  /*
   * Optional, and normally absent. The server derives it from the label by transliteration —
   * asking an admin to invent a latin slug for "Узгодження" is asking them to care about a
   * detail that exists for saved views and the database, not for them. Still accepted so an
   * integration can pin a key it already depends on.
   */
  key: dealStageKeySchema.optional(),
  label: z.string().trim().min(1).max(60),
  color: dealStageColorSchema.default('slate'),
  category: dealStatusCategorySchema,
  isDefault: z.boolean().default(false),
  position: z.number().int().nonnegative().optional(),
})

export type CreateDealStageDefinitionInput = z.infer<typeof createDealStageDefinitionInputSchema>

/**
 * `key` is absent on purpose — it is the one field that must not move, because saved views and
 * filters hold it. A used status cannot change category; the repository and database enforce it.
 */
export const updateDealStageDefinitionInputSchema = z
  .strictObject({
    label: z.string().trim().min(1).max(60),
    color: dealStageColorSchema,
    category: dealStatusCategorySchema,
    isDefault: z.boolean(),
    archived: z.boolean(),
    /** Hiding the default status: the open status that becomes the default in the same write. */
    nextDefaultKey: dealStageKeySchema,
  })
  .partial()
  .refine((input) => Object.keys(input).length > 0, 'At least one field must be updated')

export type UpdateDealStageDefinitionInput = z.infer<typeof updateDealStageDefinitionInputSchema>

/** Reordering sends the whole list — a diff of positions is not worth reconstructing server-side. */
export const reorderDealStageDefinitionsInputSchema = z.strictObject({
  keys: z.array(dealStageKeySchema).min(1),
  /** The open status that becomes the default in the same write, e.g. the new first one. */
  defaultKey: dealStageKeySchema.optional(),
})

export type ReorderDealStageDefinitionsInput = z.infer<
  typeof reorderDealStageDefinitionsInputSchema
>

/** What a fresh database starts with — the five stages the BRD names, not a fixed set. */
export const DEFAULT_DEAL_STAGES: {
  key: string
  label: string
  category: DealStatusCategory
  isDefault: boolean
  color: DealStageColor
  position: number
}[] = [
  {
    key: 'lead',
    label: 'Лід',
    category: 'not_started',
    isDefault: true,
    color: 'slate',
    position: 0,
  },
  {
    key: 'quote',
    label: 'КП',
    category: 'active',
    isDefault: false,
    color: 'blue',
    position: 1,
  },
  {
    key: 'approval',
    label: 'Узгодження',
    category: 'active',
    isDefault: false,
    color: 'amber',
    position: 2,
  },
  {
    key: 'in_production',
    label: 'Завершено успішно',
    category: 'successful',
    isDefault: false,
    color: 'green',
    position: 3,
  },
  {
    key: 'lost',
    label: 'Закрито неуспішно',
    category: 'unsuccessful',
    isDefault: false,
    color: 'red',
    position: 4,
  },
]
